import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useBooking } from "@/lib/bookingStore";
import { CALENDLY_URL } from "@/lib/constants";
import { stopLenis, startLenis } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const CALENDLY_SRC = "https://assets.calendly.com/assets/external/widget.js";

const embedUrl = () => {
    const params = new URLSearchParams({
        hide_event_type_details: "0",
        hide_gdpr_banner: "1",
        background_color: "0a0a0a",
        text_color: "ffffff",
        primary_color: "ffffff",
    });
    return `${CALENDLY_URL}?${params.toString()}`;
};

const loadCalendly = () =>
    new Promise((resolve) => {
        if (window.Calendly) return resolve();
        const existing = document.querySelector(`script[src="${CALENDLY_SRC}"]`);
        if (existing) {
            existing.addEventListener("load", () => resolve());
            return;
        }
        const script = document.createElement("script");
        script.src = CALENDLY_SRC;
        script.async = true;
        script.onload = () => resolve();
        document.body.appendChild(script);
    });

export const BookingModal = () => {
    const { open, closeBooking } = useBooking();
    const widgetRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        stopLenis();
        document.body.style.overflow = "hidden";

        let cancelled = false;
        loadCalendly().then(() => {
            if (cancelled || !widgetRef.current || !window.Calendly) return;
            widgetRef.current.innerHTML = "";
            window.Calendly.initInlineWidget({
                url: embedUrl(),
                parentElement: widgetRef.current,
            });
        });

        const onKey = (e) => e.key === "Escape" && closeBooking();
        window.addEventListener("keydown", onKey);

        return () => {
            cancelled = true;
            startLenis();
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [open, closeBooking]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    data-testid="booking-modal"
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: EASE }}
                >
                    <motion.div
                        data-testid="booking-modal-backdrop"
                        onClick={closeBooking}
                        className="absolute inset-0 bg-black/80 backdrop-blur-xl"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    />

                    <motion.div
                        className="relative z-10 flex h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#0a0a0a] shadow-2xl"
                        initial={{ opacity: 0, y: 40, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.98 }}
                        transition={{ duration: 0.55, ease: EASE }}
                    >
                        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4 md:px-8">
                            <div>
                                <p className="font-mono-x text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                                    [ Book a Call ]
                                </p>
                                <p className="font-display mt-1 text-lg font-bold tracking-tight text-white">
                                    30 minutes · Zero pitch
                                </p>
                            </div>
                            <button
                                data-testid="booking-modal-close"
                                onClick={closeBooking}
                                aria-label="Close booking"
                                className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-white transition-colors duration-300 hover:border-white/50 hover:bg-white/5"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="relative flex-1 overflow-hidden bg-[#0a0a0a]">
                            <div className="pointer-events-none absolute inset-0 grid place-items-center">
                                <span className="font-mono-x text-[11px] uppercase tracking-[0.25em] text-zinc-600">
                                    Loading calendar…
                                </span>
                            </div>
                            <div
                                ref={widgetRef}
                                data-testid="booking-calendly-inline"
                                className="relative h-full w-full"
                            />
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
